import { apiService } from './apiService';
import { API_BASE_URL } from '../config/env';
import type { Paginated } from './wearablesService';

// Contract: proposals/blood-biomarkers/api-contract.md (round 4). Every clinic
// route takes the patient's profile id (`patient.id`), the same id the
// medical-reports routes take, never `patient.userId`.

export type LabPanelStatus =
  | 'processing'
  | 'needs_patient_input'
  | 'pending_clinic_review'
  | 'confirmed'
  | 'returned'
  | 'failed';
export type LabPanelSource = 'patient_upload' | 'clinic_upload' | 'manual' | 'chat';
export type RecordedBy = 'patient' | 'clinician' | 'system';
export type ConfirmedBy = 'clinician' | 'admin' | null;
export type EditedBy = 'patient' | 'clinician' | null;
export type NameCheck = 'match' | 'mismatch' | 'unknown';
export type Blocker = 'name_mismatch' | 'collected_on_missing';
export type ReturnReason = 'not_a_lab_report' | 'unreadable' | 'wrong_patient' | 'duplicate' | 'other';
export type PanelError = 'unreadable_file' | 'no_rows_found' | 'extraction_failed' | 'too_many_pages';
export type CheckStatus = 'ok' | 'flagged' | 'edited' | 'rejected';
export type CheckReason = 'implausible_value' | 'unknown_unit' | 'value_not_on_page' | 'unparsed_value';
export type RangeStatus = 'low' | 'high' | 'in_range' | 'unknown';
export type LabFlag = 'high' | 'low' | null;
export type Comparator = '=' | '<' | '>' | '<=' | '>=';
export type ExcludedReason = 'unit_not_convertible' | 'method_differs' | 'text_result';
export type BiomarkerCategory =
  | 'glucose'
  | 'lipids'
  | 'kidney'
  | 'liver'
  | 'electrolytes'
  | 'thyroid'
  | 'iron'
  | 'vitamins'
  | 'blood_count'
  | 'inflammation'
  | 'hormones'
  | 'muscle'
  | 'urine'
  | 'other';

export interface LabPanelFile {
  id: number;
  order: number;
  content_type: string;
  url: string;
  pages: number | null;
}

export interface LabPanelCounts {
  results: number;
  mapped: number;
  unmapped: number;
  flagged: number;
  out_of_range: number;
  rejected: number;
}

export interface ClinicReview {
  available: boolean;
  sent_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

export interface LabPanel {
  id: number;
  status: LabPanelStatus;
  source: LabPanelSource;
  recorded_by: RecordedBy;
  collected_on: string | null;
  lab_name: string;
  files: LabPanelFile[];
  medical_report_id: number | null;
  counts: LabPanelCounts;
  name_check: NameCheck;
  blockers: Blocker[];
  clinic_review: ClinicReview;
  return_reason: ReturnReason | null;
  clinician_note: string | null;
  processing: { model: string; location: string; pages: number | null; attempts: number } | null;
  error: PanelError | null;
  can_edit: boolean;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
  extracted_at: string | null;
  confirmed_at: string | null;
  confirmed_by: ConfirmedBy;
  // Clinic audiences only (contract §3.6): absent on older backends.
  /** Revision of the rows; a reprocess or a remap creates a new one. */
  revision?: number;
  /** A validated panel waiting for its new validation after a reprocess or a remap. */
  revalidation?: boolean;
  /** Mode B (model) runs: how many were used and how many are left. */
  reprocess?: { used: number; limit: number; remaining: number };
  /** Computed by the server for the requesting user: mode A may run now. */
  can_remap?: boolean;
  /** Computed by the server for the requesting user: mode B may run now (role, document, limit). */
  can_reprocess?: boolean;
}

export type ReprocessMode = 'remap' | 'reprocess';
export type ReprocessRunStatus = 'running' | 'done' | 'failed';

export interface ReprocessRunRow {
  id: number;
  code: string | null;
  raw_name: string;
  raw_value: string;
  raw_unit: string | null;
  value: string | null;
  unit: string | null;
  check_status: CheckStatus;
  check_reason: CheckReason | null;
  edited_by: EditedBy;
  page: number | null;
}

export interface ReprocessRun {
  id: number;
  mode: ReprocessMode;
  status: ReprocessRunStatus;
  triggered_by: { id: number; name: string; role: string } | null;
  started_at: string;
  finished_at: string | null;
  status_before: LabPanelStatus;
  revision_before: number;
  revision_after: number | null;
  model: string | null;
  prompt_version: string | null;
  catalog_version: string | null;
  previous_validation: {
    revision: number;
    confirmed_at: string;
    confirmed_by: ConfirmedBy;
    reviewed_by: { id: number; name: string } | null;
    clinician_note: string | null;
  } | null;
  rows_before: ReprocessRunRow[];
  rows_after: ReprocessRunRow[];
  error: string | null;
}

export interface ReprocessRunsResponse {
  reprocess: { used: number; limit: number; remaining: number };
  runs: ReprocessRun[];
}

export interface BiomarkerResult {
  id: number;
  panel_id: number;
  code: string | null;
  name: string;
  category: BiomarkerCategory;
  raw: { name: string; value: string; unit: string | null; reference: string | null; page: number | null };
  value: number | null;
  comparator: Comparator;
  value_text: string | null;
  unit: string;
  value_canonical: number | null;
  ref_low: number | null;
  ref_high: number | null;
  lab_flag: LabFlag;
  range_status: RangeStatus;
  method: string | null;
  check_status: CheckStatus;
  check_reason: CheckReason | null;
  edited_by: EditedBy;
  collected_on: string | null;
}

/** A row create or edit also returns the panel's new `updated_at`, to send as
 *  the next `expected_updated_at` without re-reading the panel. */
export interface BiomarkerResultWrite extends BiomarkerResult {
  panel_updated_at?: string;
}

export interface ResultGroup {
  category: BiomarkerCategory;
  label: string;
  results: BiomarkerResult[];
}

export interface LabPanelDetail extends LabPanel {
  groups: ResultGroup[];
}

export interface SummaryPoint {
  result_id: number;
  panel_id: number;
  value: number | null;
  comparator: Comparator;
  value_text: string | null;
  ref_low: number | null;
  ref_high: number | null;
  lab_flag: LabFlag;
  range_status: RangeStatus;
  check_status: CheckStatus;
  collected_on: string;
}

export interface BiomarkerSummary {
  code: string;
  name: string;
  category: BiomarkerCategory;
  unit: string;
  latest: SummaryPoint;
  previous: SummaryPoint | null;
  change: { absolute: number; percent: number | null; days: number } | null;
  history: { collected_on: string; value: number }[];
  n_results: number;
}

export interface UnmappedSummary {
  name: string;
  unit: string;
  latest: SummaryPoint;
}

export interface CategoryLabel {
  code: BiomarkerCategory;
  label: string;
}

export interface BiomarkersResponse {
  categories: CategoryLabel[];
  biomarkers: BiomarkerSummary[];
  unmapped: UnmappedSummary[];
  last_panel: { id: number; collected_on: string | null; status: LabPanelStatus } | null;
  pending: {
    count: number;
    needs_patient_input: number;
    pending_clinic_review: number;
    returned: number;
    clinic_review_available: boolean;
  };
}

export interface HistoryPoint extends SummaryPoint {
  lab_name: string | null;
}

export interface ExcludedPoint {
  result_id: number;
  panel_id: number;
  collected_on: string;
  value: number | null;
  unit: string;
  value_text: string | null;
  lab_name: string | null;
  reason: ExcludedReason;
}

export interface BiomarkerHistory {
  biomarker: import('./biomarkerCatalogService').BiomarkerCatalogEntry;
  unit: string;
  points: HistoryPoint[];
  excluded: ExcludedPoint[];
}

export interface QueueRow extends LabPanel {
  patient: {
    id: number;
    first_name: string;
    last_name: string;
    avatar_url: string | null;
    clinic_id: number | null;
    assigned_professional: { id: number; name: string } | null;
  };
  waiting_since: string;
  patient_edited_since_sent: boolean;
}

export type QueueAssigned = 'me' | 'clinic' | 'unassigned' | 'none';

export interface LabConsent {
  required: boolean;
  granted: boolean;
  text_version: string;
  granted_at: string | null;
  source?: 'patient' | 'clinic';
  attested_by?: string | null;
}

export interface ManualResultInput {
  code: string | null;
  name?: string;
  value?: number | string;
  value_text?: string;
  comparator?: Comparator;
  unit: string;
  ref_low?: number | null;
  ref_high?: number | null;
}

export interface ManualPanelInput {
  collected_on: string;
  lab_name: string;
  results: ManualResultInput[];
}

export interface PanelPatch {
  collected_on?: string;
  lab_name?: string;
  acknowledge_name_mismatch?: true;
  confirm?: true;
  clinician_note?: string;
  return?: { reason: ReturnReason; note?: string };
  expected_updated_at?: string;
}

export interface ResultPatch {
  code?: string | null;
  value?: number | string;
  comparator?: Comparator;
  value_text?: string | null;
  unit?: string;
  ref_low?: number | null;
  ref_high?: number | null;
  reject?: boolean;
  expected_updated_at?: string;
}

export interface UploadInput {
  files: File[];
  collected_on?: string;
  lab_name?: string;
  consent_attested?: boolean;
}

// The upload answers 202 for a new panel and 200 for a duplicate; the caller
// tells them apart by whether `id` was already in its list, so one return type
// is enough.
export interface UploadResult {
  panel: LabPanel;
}

const patientBase = (patientId: number | string) =>
  `${API_BASE_URL}/clinic/patients/${patientId}`;

export const labPanelsService = {
  listPanels: async (
    patientId: number | string,
    query: { page?: number; perpage?: number; status?: LabPanelStatus } = {},
  ): Promise<Paginated<LabPanel>> => {
    const params = new URLSearchParams();
    params.set('page', String(query.page ?? 1));
    params.set('perpage', String(query.perpage ?? 20));
    if (query.status) params.set('status', query.status);
    return apiService.get(`${patientBase(patientId)}/lab-panels/?${params}`);
  },

  getPanel: async (patientId: number | string, panelId: number | string): Promise<LabPanelDetail> =>
    apiService.get(`${patientBase(patientId)}/lab-panels/${panelId}/`),

  uploadPanel: async (patientId: number | string, input: UploadInput): Promise<LabPanel> => {
    const form = new FormData();
    if (input.files.length === 1 && input.files[0].type === 'application/pdf') {
      form.append('file', input.files[0]);
    } else {
      input.files.forEach((file) => form.append('files', file));
    }
    if (input.collected_on) form.append('collected_on', input.collected_on);
    if (input.lab_name) form.append('lab_name', input.lab_name);
    if (input.consent_attested) form.append('consent_attested', 'true');
    return apiService.postForm(`${patientBase(patientId)}/lab-panels/`, form);
  },

  createManualPanel: async (patientId: number | string, input: ManualPanelInput): Promise<LabPanel> =>
    apiService.post(`${patientBase(patientId)}/lab-panels/manual/`, input),

  patchPanel: async (
    patientId: number | string,
    panelId: number | string,
    patch: PanelPatch,
  ): Promise<LabPanelDetail> =>
    apiService.patch(`${patientBase(patientId)}/lab-panels/${panelId}/`, patch),

  addResult: async (
    patientId: number | string,
    panelId: number | string,
    row: ManualResultInput,
  ): Promise<BiomarkerResultWrite> =>
    apiService.post(`${patientBase(patientId)}/lab-panels/${panelId}/results/`, row),

  patchResult: async (
    patientId: number | string,
    panelId: number | string,
    resultId: number | string,
    patch: ResultPatch,
  ): Promise<BiomarkerResultWrite> =>
    apiService.patch(`${patientBase(patientId)}/lab-panels/${panelId}/results/${resultId}/`, patch),

  retryPanel: async (patientId: number | string, panelId: number | string): Promise<LabPanel> =>
    apiService.post(`${patientBase(patientId)}/lab-panels/${panelId}/retry/`, {}),

  // Contract §3.6. Mode A maps the model's rows against the catalog again, no
  // model call; mode B sends the document to the model again and discards the
  // corrections. Both put the panel back in the queue.
  remapPanel: async (patientId: number | string, panelId: number | string, expectedUpdatedAt?: string): Promise<LabPanelDetail> =>
    apiService.post(`${patientBase(patientId)}/lab-panels/${panelId}/remap/`, expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}),

  reprocessPanel: async (patientId: number | string, panelId: number | string, expectedUpdatedAt?: string): Promise<LabPanel> =>
    apiService.post(`${patientBase(patientId)}/lab-panels/${panelId}/reprocess/`, expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}),

  listRuns: async (patientId: number | string, panelId: number | string): Promise<ReprocessRunsResponse> =>
    apiService.get(`${patientBase(patientId)}/lab-panels/${panelId}/runs/`),

  setReprocessLimit: async (patientId: number | string, panelId: number | string, limit: number): Promise<LabPanelDetail> =>
    apiService.patch(`${patientBase(patientId)}/lab-panels/${panelId}/`, { reprocess_limit: limit }),

  deletePanel: async (patientId: number | string, panelId: number | string): Promise<void> => {
    await apiService.delete(`${patientBase(patientId)}/lab-panels/${panelId}/`);
  },

  getBiomarkers: async (patientId: number | string): Promise<BiomarkersResponse> =>
    apiService.get(`${patientBase(patientId)}/biomarkers/`),

  getBiomarkerHistory: async (patientId: number | string, code: string): Promise<BiomarkerHistory> =>
    apiService.get(`${patientBase(patientId)}/biomarkers/${encodeURIComponent(code)}/`),

  getConsent: async (patientId: number | string): Promise<LabConsent> =>
    apiService.get(`${patientBase(patientId)}/consents/lab_upload/`),

  attestConsent: async (patientId: number | string, textVersion: string): Promise<LabConsent> =>
    apiService.post(`${patientBase(patientId)}/consents/lab_upload/`, {
      text_version: textVersion,
      attested: true,
    }),

  // Clinic-wide validation queue (contract §3.1). `status` defaults on the
  // server to the two states that are work for the clinic.
  listQueue: async (
    query: { assigned?: QueueAssigned; status?: LabPanelStatus; page?: number; perpage?: number } = {},
  ): Promise<Paginated<QueueRow>> => {
    const params = new URLSearchParams();
    params.set('page', String(query.page ?? 1));
    params.set('perpage', String(query.perpage ?? 50));
    if (query.assigned) params.set('assigned', query.assigned);
    if (query.status) params.set('status', query.status);
    return apiService.get(`${API_BASE_URL}/clinic/lab-panels/?${params}`);
  },
};

/** The stable code the backend puts on every 4xx body, or null. */
export const errorCode = (error: unknown): string | null => {
  const code = (error as { response?: { code?: unknown } } | null)?.response?.code;
  return typeof code === 'string' ? code : null;
};

/** Extra fields the backend attaches to some errors (file_index, row_index, flagged_results…). */
export const errorBody = (error: unknown): Record<string, unknown> => {
  const body = (error as { response?: unknown } | null)?.response;
  return body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
};
